import type { Cable } from '../../types';
import type { DeviceModel, LogicIssue, LogicIssueCode, LogicSeverity } from '../../types/deviceModel';
import { clampUnitInterval, isBatPortKind, isNegativePowerPole, isPositivePowerPole, isPvPortKind } from '../../types/deviceModel';
import { parsePortEndpointId } from '../../utils/portNamingSchemes';
import { placedIdFromEndpoint } from '../../utils/cableGeometry';
import type { ResolvedDevice } from './resolveModel';

export interface TwoTerminal {
  nodeId: string;
  plusNet: string;
  minusNet: string;
  voc: number;
  vmp: number;
  isc: number;
  pmax: number;
  volts: number;
  ah: number;
}

export interface ReducedBranch {
  voc: number;
  vmp: number;
  isc: number;
  pmax: number;
  volts: number;
  ah: number;
  moduleCount: number;
  warnings: string[];
}

type IssueFn = (
  code: LogicIssueCode,
  severity: LogicSeverity,
  message: string,
  extras?: Partial<LogicIssue>
) => void;

function portKey(nodeId: string, number: number, kind: string, face?: string): string {
  return `${nodeId}:${face || 'front'}:${number}:${kind}`;
}

function unionFind() {
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    if (!parent.has(x)) parent.set(x, x);
    const p = parent.get(x)!;
    if (p !== x) {
      const r = find(p);
      parent.set(x, r);
      return r;
    }
    return x;
  };
  const join = (a: string, b: string) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  return { find, join };
}

function endpointPort(nodes: Map<string, ResolvedDevice>, endpoint: string) {
  const placedId = placedIdFromEndpoint(endpoint);
  const parsed = parsePortEndpointId(endpoint);
  if (!placedId || !parsed) return null;
  const node = nodes.get(placedId);
  if (!node) return null;
  const port = node.ports.find(
    (p) =>
      p.number === parsed.number &&
      (!parsed.kind || String(p.kind) === parsed.kind) &&
      (!parsed.face || (p.face || 'front') === parsed.face)
  );
  if (!port) return null;
  return { node, port, key: portKey(node.nodeId, port.number, String(port.kind), port.face) };
}

export function buildPolarityNets(
  nodes: Map<string, ResolvedDevice>,
  cables: Cable[],
  family: 'pv' | 'bat',
  issue: IssueFn
) {
  const uf = unionFind();
  const kindOk = family === 'pv' ? isPvPortKind : isBatPortKind;
  const seenPairs = new Set<string>();

  cables.forEach((cable) => {
    if (cable.type !== 'power') return;
    const a = endpointPort(nodes, cable.from);
    const b = endpointPort(nodes, cable.to);
    if (!a || !b) return;
    if (a.node.excluded || b.node.excluded) {
      if ((kindOk(String(a.port.kind)) || kindOk(String(b.port.kind))) && (a.node.excluded || b.node.excluded)) {
        if (kindOk(String(a.port.kind)) && kindOk(String(b.port.kind)) && (a.node.excluded !== b.node.excluded)) {
          issue(family === 'pv' ? 'PV_OPEN_STRING' : 'BAT_POLARITY', 'error', 'A device mid-string is excluded from Logic.', {
            cableId: cable.id,
            equipmentId: a.node.excluded ? a.node.nodeId : b.node.nodeId,
          });
        }
      }
      return;
    }
    if (!kindOk(String(a.port.kind)) || !kindOk(String(b.port.kind))) return;
    const pair = [a.key, b.key].sort().join('|');
    if (seenPairs.has(pair)) return;
    seenPairs.add(pair);
    uf.join(a.key, b.key);
    uf.join(a.key, a.key);
    uf.join(b.key, b.key);
  });

  nodes.forEach((node) => {
    if (node.excluded) return;
    node.ports.forEach((p) => {
      if (kindOk(String(p.kind))) uf.join(portKey(node.nodeId, p.number, String(p.kind), p.face), portKey(node.nodeId, p.number, String(p.kind), p.face));
    });
  });

  return uf;
}

function collectTerminals(
  nodes: Map<string, ResolvedDevice>,
  family: 'pv' | 'bat',
  uf: ReturnType<typeof unionFind>
): TwoTerminal[] {
  const kindPlus = family === 'pv' ? 'pvPlus' : 'batPlus';
  const kindMinus = family === 'pv' ? 'pvMinus' : 'batMinus';
  const out: TwoTerminal[] = [];
  nodes.forEach((node) => {
    if (node.excluded) return;
    const plus = node.ports.filter((p) => String(p.kind) === kindPlus);
    const minus = node.ports.filter((p) => String(p.kind) === kindMinus);
    if (plus.length === 0 || minus.length === 0) return;
    if (family === 'pv' && !node.model.isSolar) return;
    if (family === 'bat' && !node.model.isBattery) return;
    const p = plus[0];
    const m = minus[0];
    const plusNet = uf.find(portKey(node.nodeId, p.number, String(p.kind), p.face));
    const minusNet = uf.find(portKey(node.nodeId, m.number, String(m.kind), m.face));
    const voc = family === 'pv' ? node.model.pvVoc || 0 : node.model.nominalVoltage || 0;
    const vmp = family === 'pv' ? node.model.pvVmp || voc : voc;
    const isc = family === 'pv' ? node.model.pvIsc || 0 : 0;
    const pmax = family === 'pv' ? node.model.pvStcWatts || 0 : 0;
    const volts = family === 'bat' ? node.model.nominalVoltage || 0 : voc;
    const ah = family === 'bat' ? node.model.capacityAh || 0 : 0;
    if (family === 'pv' && (!(voc > 0) || !(vmp > 0))) return;
    if (family === 'bat' && (!(volts > 0) || !(ah > 0))) return;
    out.push({
      nodeId: node.nodeId,
      plusNet,
      minusNet,
      voc,
      vmp,
      isc,
      pmax,
      volts,
      ah,
    });
  });
  return out;
}

interface Edge {
  id: string;
  a: string;
  b: string;
  voc: number;
  vmp: number;
  isc: number;
  pmax: number;
  volts: number;
  ah: number;
  count: number;
  warnings: string[];
}

function reduceSeriesParallel(
  edges: Edge[],
  start: string,
  end: string
): { ok: boolean; branch?: ReducedBranch; reason?: string } {
  if (start === end) return { ok: false, reason: 'short' };
  let list = edges.map((e) => ({ ...e, warnings: [...e.warnings] }));
  const seen = new Set<string>();

  for (let guard = 0; guard < 64; guard++) {
    const key = list.map((e) => `${e.a}|${e.b}|${e.count}|${e.voc}|${e.isc}`).sort().join(';');
    if (seen.has(key)) break;
    seen.add(key);

    const byPair = new Map<string, Edge[]>();
    list.forEach((e) => {
      const pair = e.a < e.b ? `${e.a}||${e.b}` : `${e.b}||${e.a}`;
      const arr = byPair.get(pair) || [];
      arr.push(e);
      byPair.set(pair, arr);
    });
    let changed = false;
    const next: Edge[] = [];
    const consumed = new Set<string>();
    byPair.forEach((group) => {
      if (group.length > 1) {
        group.forEach((g) => consumed.add(g.id));
        const vocs = group.map((g) => g.voc);
        const warnings = group.flatMap((g) => g.warnings);
        const vocMax = Math.max(...vocs);
        const vocMin = Math.min(...vocs);
        if (vocMax > 0 && vocMin > 0 && Math.abs(vocMax - vocMin) / vocMax > 0.05) {
          warnings.push('Parallel branches have different voltages.');
        }
        next.push({
          id: `par-${group.map((g) => g.id).join('+')}`,
          a: group[0].a,
          b: group[0].b,
          voc: vocMax,
          vmp: Math.max(...group.map((g) => g.vmp)),
          isc: group.reduce((s, g) => s + g.isc, 0),
          pmax: group.reduce((s, g) => s + g.pmax, 0),
          volts: vocMax,
          ah: group.reduce((s, g) => s + g.ah, 0),
          count: group.reduce((s, g) => s + g.count, 0),
          warnings,
        });
        changed = true;
      }
    });
    list.forEach((e) => {
      if (!consumed.has(e.id)) next.push(e);
    });
    if (changed) {
      list = next;
      continue;
    }

    const degree = new Map<string, Edge[]>();
    list.forEach((e) => {
      degree.set(e.a, [...(degree.get(e.a) || []), e]);
      degree.set(e.b, [...(degree.get(e.b) || []), e]);
    });
    let seriesMerged = false;
    for (const [net, incident] of Array.from(degree.entries())) {
      if (net === start || net === end) continue;
      if (incident.length !== 2) continue;
      const [e1, e2] = incident;
      const other1 = e1.a === net ? e1.b : e1.a;
      const other2 = e2.a === net ? e2.b : e2.a;
      if (other1 === other2) continue;
      const warnings = [...e1.warnings, ...e2.warnings];
      if (e1.isc > 0 && e2.isc > 0 && Math.abs(e1.isc - e2.isc) / Math.max(e1.isc, e2.isc) > 0.08) {
        warnings.push('Series devices have different current ratings; using the lower Isc.');
      }
      if (e1.ah > 0 && e2.ah > 0 && Math.abs(e1.ah - e2.ah) / Math.max(e1.ah, e2.ah) > 0.08) {
        warnings.push('Series batteries have different Ah; using the lower Ah.');
      }
      list = list.filter((e) => e.id !== e1.id && e.id !== e2.id);
      list.push({
        id: `ser-${e1.id}-${e2.id}`,
        a: other1,
        b: other2,
        voc: e1.voc + e2.voc,
        vmp: e1.vmp + e2.vmp,
        isc: e1.isc > 0 && e2.isc > 0 ? Math.min(e1.isc, e2.isc) : e1.isc || e2.isc,
        pmax: e1.pmax + e2.pmax,
        volts: e1.volts + e2.volts,
        ah: e1.ah > 0 && e2.ah > 0 ? Math.min(e1.ah, e2.ah) : e1.ah || e2.ah,
        count: e1.count + e2.count,
        warnings,
      });
      seriesMerged = true;
      break;
    }
    if (seriesMerged) continue;
    break;
  }

  if (list.length === 1) {
    const e = list[0];
    const connects = (e.a === start && e.b === end) || (e.a === end && e.b === start);
    if (connects) {
      return {
        ok: true,
        branch: {
          voc: e.voc,
          vmp: e.vmp,
          isc: e.isc,
          pmax: e.pmax,
          volts: e.volts,
          ah: e.ah,
          moduleCount: e.count,
          warnings: e.warnings,
        },
      };
    }
  }
  if (list.length === 0) return { ok: false, reason: 'open' };
  return { ok: false, reason: 'unsolvable' };
}

export function analyzeFamilyAtPair(
  nodes: Map<string, ResolvedDevice>,
  cables: Cable[],
  family: 'pv' | 'bat',
  plusKey: string,
  minusKey: string,
  issue: IssueFn
): { branch: ReducedBranch | null; reason?: string } {
  nodes.forEach((node) => {
    if (node.excluded) return;
    if (family === 'pv' && node.model.isSolar && (!(node.model.pvVoc! > 0) || !(node.model.pvVmp! > 0))) {
      issue('UNMODELED', 'warning', `${node.name} has no Voc/Vmp; it is skipped in the PV string.`, { equipmentId: node.nodeId });
    }
    if (family === 'bat' && node.model.isBattery && (!(node.model.nominalVoltage! > 0) || !(node.model.capacityAh! > 0))) {
      issue('UNMODELED', 'warning', `${node.name} has no voltage/Ah; it is skipped in the battery bank.`, { equipmentId: node.nodeId });
    }
  });
  const uf = buildPolarityNets(nodes, cables, family, issue);
  const plusNet = uf.find(plusKey);
  const minusNet = uf.find(minusKey);
  const terminals = collectTerminals(nodes, family, uf);
  terminals.forEach((t) => {
    if (t.plusNet === t.minusNet) {
      issue(family === 'pv' ? 'PV_POLARITY' : 'BAT_POLARITY', 'error', 'Positive and negative poles are shorted together.', {
        equipmentId: t.nodeId,
      });
    }
  });
  const edges: Edge[] = terminals
    .filter((t) => t.plusNet !== t.minusNet)
    .map((t) => ({
      id: t.nodeId,
      a: t.plusNet,
      b: t.minusNet,
      voc: t.voc,
      vmp: t.vmp,
      isc: t.isc,
      pmax: t.pmax,
      volts: t.volts,
      ah: t.ah,
      count: 1,
      warnings: [],
    }));
  const touches = (net: string) => edges.some((e) => e.a === net || e.b === net);
  if (!touches(plusNet) && !touches(minusNet)) {
    return { branch: null, reason: 'unused' };
  }
  const reduced = reduceSeriesParallel(edges, plusNet, minusNet);
  if (!reduced.ok) {
    const code = family === 'pv'
      ? reduced.reason === 'open'
        ? 'PV_OPEN_STRING'
        : reduced.reason === 'short'
          ? 'PV_POLARITY'
          : 'PV_UNSOLVABLE'
      : 'BAT_POLARITY';
    if (!(family === 'bat' && reduced.reason === 'open')) {
      issue(code, 'error', reduced.reason === 'open' ? 'String is open (a pole is not connected).' : reduced.reason === 'short' ? 'Array is shorted.' : 'Wiring is not a simple series/parallel string.', {});
    }
    return { branch: null, reason: reduced.reason };
  }
  return { branch: reduced.branch || null };
}

export function findSharedPvTrackers(
  nodes: Map<string, ResolvedDevice>,
  cables: Cable[],
  issue: IssueFn
) {
  const uf = buildPolarityNets(nodes, cables, 'pv', () => {});
  const occupied = new Map<string, string[]>();
  nodes.forEach((node) => {
    if (node.excluded || !node.model.isInverter) return;
    node.model.powerInputs.filter((i) => i.kind === 'pv' && i.enabled !== false).forEach((input) => {
      const pair = inverterMpptPorts(node, input.mpptId || input.id);
      if (!pair) return;
      const plusNet = uf.find(pair.plusKey);
      const minusNet = uf.find(pair.minusKey);
      if (plusNet === minusNet) return;
      const key = `${plusNet}|${minusNet}`;
      const list = occupied.get(key) || [];
      list.push(`${node.name} ${input.mpptId || input.id}`);
      occupied.set(key, list);
    });
  });
  occupied.forEach((list) => {
    if (list.length > 1) {
      issue('PV_UNSOLVABLE', 'error', `One PV string lands on more than one MPPT (${list.join(', ')}).`);
    }
  });
}

export function inverterMpptPorts(node: ResolvedDevice, mpptId: string) {
  const input = node.model.powerInputs.find((i) => i.kind === 'pv' && (i.mpptId || i.id) === mpptId);
  if (!input) return null;
  const plusN = input.plusPortNumber;
  const minusN = input.minusPortNumber;
  const plus = node.ports.find((p) => String(p.kind) === 'pvPlus' && (plusN == null || p.number === plusN) && (!input.plusFace || (p.face || 'front') === input.plusFace));
  const minus = node.ports.find((p) => String(p.kind) === 'pvMinus' && (minusN == null || p.number === minusN) && (!input.minusFace || (p.face || 'front') === input.minusFace));
  if (!plus || !minus) return null;
  return {
    input,
    plus,
    minus,
    plusKey: portKey(node.nodeId, plus.number, String(plus.kind), plus.face),
    minusKey: portKey(node.nodeId, minus.number, String(minus.kind), minus.face),
  };
}

export function inverterBatteryPorts(node: ResolvedDevice) {
  const input = node.model.powerInputs.find((i) => i.kind === 'battery');
  const plus = node.ports.find((p) => String(p.kind) === 'batPlus' && (input?.plusPortNumber == null || p.number === input.plusPortNumber));
  const minus = node.ports.find((p) => String(p.kind) === 'batMinus' && (input?.minusPortNumber == null || p.number === input.minusPortNumber));
  if (!plus || !minus) return null;
  return {
    input,
    plus,
    minus,
    plusKey: portKey(node.nodeId, plus.number, String(plus.kind), plus.face),
    minusKey: portKey(node.nodeId, minus.number, String(minus.kind), minus.face),
  };
}

export function usableBatteryWh(model: DeviceModel, volts: number, ah: number): number {
  if (!(volts > 0) || !(ah > 0)) return 0;
  return volts * ah * clampUnitInterval(model.usableFraction, 0.8);
}

export { portKey };
export { isPositivePowerPole, isNegativePowerPole };
